import { useEffect, useState } from 'react';
import { AuthService } from '../services/authService';
import { docInterpretationService } from '../services/docInterpretationService';
import type { DocInterpretationAvailability } from '../types/docInterpretation';

/**
 * Om dokumenttolking skal vises for tenanten (TW 36422795).
 *
 * Funksjonen krever migrasjon 127 i tenant-databasen og at IntegrasjonsPartner har aktivert den.
 * Tenanter uten tabellene skal ikke se menyvalgene eller knappen «Les inn leverandørdokument».
 *
 * Svaret hentes én gang per innlogging (token) og deles av alle komponentene (menyen, dashbordet og
 * bestillingslisten), så en ny innlogging mot en annen tenant leser det på nytt. Skjult til svaret er
 * kommet, og skjult hvis kallet feiler.
 */
const HIDDEN: DocInterpretationAvailability = { installed: false, enabled: false };
let cached: Promise<DocInterpretationAvailability> | null = null;
let cachedForToken: string | null = null;

const load = () => {
  const token = AuthService.getToken();
  if (!cached || cachedForToken !== token) {
    cachedForToken = token;
    cached = docInterpretationService.getAvailability().catch(err => {
      console.warn('Kunne ikke hente status for dokumenttolking, skjuler funksjonen:', err);
      cached = null;
      return HIDDEN;
    });
  }
  return cached;
};

/** Leses på nytt neste gang, f.eks. etter at funksjonen er aktivert på innstillingssiden. */
export function invalidateDocInterpretationAvailability(): void {
  cached = null;
}

/** Modulene som tilhører dokumenttolking, og når de vises. */
export function isDocInterpretationModuleVisible(moduleId: string | undefined, availability: DocInterpretationAvailability): boolean {
  if (moduleId === 'doc-interpretation-settings') return availability.installed;
  if (moduleId === 'doc-interpretation-log') return availability.installed && availability.enabled;
  return true;
}

/** `loaded` er false til svaret er kommet; til da regnes funksjonen som skjult. */
export function useDocInterpretationAvailability(): DocInterpretationAvailability & { loaded: boolean } {
  const [availability, setAvailability] = useState<DocInterpretationAvailability & { loaded: boolean }>({ ...HIDDEN, loaded: false });

  useEffect(() => {
    let cancelled = false;
    load().then(result => { if (!cancelled) setAvailability({ ...result, loaded: true }); });
    return () => { cancelled = true; };
  }, []);

  return availability;
}
